import { useEffect, useRef } from "react"

import { ReactComponent as Logo } from "@brand_assets/svg/logo.svg"
import { Box, Grid } from "@mui/material"
import LoginAsStudent from "components/module/loginAsStudent"
import LoginAsTeacher from "components/module/loginAsTeacher"
import SignAsStudent from "components/module/signAsStudent"
import Finalization from "components/module/signAsStudent/finalization"
import SignByEmail from "components/module/signAsStudent/signByEmail"
import SignAsTeacher from "components/module/signAsTeacher"
import AddSchoolForm from "components/module/signAsTeacher/addSchool/AddSchoolForm"
import FindSchoolForm from "components/module/signAsTeacher/findSchool"
import PersonalForm from "components/module/signAsTeacher/personalForm"
import { useDirectLink } from "hooks/directLink"
import { useTranslation } from "react-i18next"
import { visuallyHiddenSx } from "styles/common/typography"
import { regexPrivateIP } from "utils/constants"

import BackgroundImageLeft from "../backgroundImageLeft"

interface IProps {
  desktopView: boolean
  activePage: number
  registerType: "signUp" | "login"
}

const isLocalOrNetworkAccess = (origin: string) => {
  if (origin.includes("localhost") || origin.includes("127.0.0.1") || origin.includes("10.0.2.2")) {
    return true
  }
  try {
    const hostname = new URL(origin).hostname
    return regexPrivateIP.test(hostname)
  } catch {
    return false
  }
}

const RegisterLayout = ({ desktopView, activePage, registerType }: IProps) => {
  const location = window.location.origin
  const isLocal = isLocalOrNetworkAccess(location)
  const { t } = useTranslation()
  const contentRef = useRef<HTMLDivElement>(null)
  const isInitialMount = useRef(true)

  useDirectLink()

  const isTeacherFlow =
    (isLocal && process.env.REACT_APP_USER === "teacher") ||
    location.includes("teacher")
  const isStudentFlow =
    (isLocal && process.env.REACT_APP_USER === "student") ||
    location.includes("student")

  let totalSteps: number | undefined
  if (registerType === "signUp") {
    if (isTeacherFlow) totalSteps = 4
    else if (isStudentFlow) totalSteps = 3
  }

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    contentRef.current?.querySelector<HTMLElement>("h1")?.focus()
  }, [activePage])

  return (
    <Grid container minHeight="100vh" width="100%">
      {desktopView && <BackgroundImageLeft />}
      <Grid
        width={{ desktop: "", mobile: "100%" }}
        px={{ desktop: 0, mobile: "10px" }}
        alignItems="center"
        justifyContent={{ desktop: "center", mobile: "stretch" }}
        flexDirection={{ desktop: "row", mobile: "column" }}
        display="flex"
        textAlign="center"
        flex="1 0 auto"
      >
        <Box maxWidth="100%" width="426px">
          {!desktopView && (
            <Box textAlign="left" my={5} width="115px">
              <Logo aria-hidden="true" />
            </Box>
          )}
          {totalSteps && (
            <Box sx={visuallyHiddenSx} aria-live="polite">
              {t("noAuthFlow.stepOfSteps", {
                current: activePage,
                total: totalSteps,
              })}
            </Box>
          )}
          <Box ref={contentRef}>
            {registerType === "signUp" &&
              isTeacherFlow &&
              ((activePage === 1 && <SignAsTeacher />) ||
                (activePage === 2 && <PersonalForm />) ||
                (activePage === 3 && <FindSchoolForm />) ||
                (activePage === 4 && <AddSchoolForm />))}

            {registerType === "signUp" &&
              isStudentFlow &&
              ((activePage === 1 && <SignAsStudent />) ||
                (activePage === 2 && <SignByEmail />) ||
                (activePage === 3 && <Finalization />))}

            {registerType === "login" && isTeacherFlow && <LoginAsTeacher />}

            {registerType === "login" && isStudentFlow && <LoginAsStudent />}
          </Box>
        </Box>
      </Grid>
    </Grid>
  )
}

export default RegisterLayout
